import type { PostgrestError } from "@supabase/supabase-js"
import type { SyncAction, SyncableTable } from "./action"

/**
 * Classification of sync-up failures into "try again" and "this will never work".
 *
 * Failed sync actions stay in the pending queue and are retried forever, which is
 * the right behavior for an offline client but a trap for a record the server has
 * definitively refused: the retry can't change the outcome, so the queue never
 * drains, `syncUp` throws on every pass, and the client never syncs again.
 *
 * Rather than enumerating individual failure modes, we treat any action the server
 * has rejected MAX_SYNC_ATTEMPTS times in a row as unsyncable, discard it, and
 * report it to Sentry.
 */

/**
 * How many consecutive server rejections an action gets before we give up on it.
 *
 * Sync-up retries with exponential backoff (1s, then 2s → 60s), so five attempts is
 * roughly a minute of trying. That matters because a rejection is not always about
 * the record itself: an action can fail only because the row it references is still
 * queued behind it (a foreign key to a blob whose own insert failed transiently, for
 * example). Those clear as soon as the predecessor lands, well inside five passes.
 */
export const MAX_SYNC_ATTEMPTS = 5

/**
 * SQLSTATE classes that describe the state of the *server* rather than anything
 * wrong with the payload we sent, so they say nothing about whether this record can
 * ever sync:
 *
 *  - `08` connection exception
 *  - `40` transaction rollback (deadlock, serialization failure)
 *  - `53` insufficient resources
 *  - `57` operator intervention (statement timeout, query cancelled, shutdown)
 *  - `58` external system error
 */
const TRANSIENT_SQLSTATE_CLASSES = ['08', '40', '53', '57', '58']

/**
 * PostgREST-level codes that resolve without the payload changing: an expired JWT is
 * refreshed by the Supabase client before the next attempt, and a cold schema cache
 * reloads on its own.
 */
const TRANSIENT_CODES = new Set(['PGRST301', 'PGRST002'])

/**
 * Whether the server actually looked at this action and refused it, as opposed to
 * the request never getting a verdict.
 *
 * A transport failure — offline, DNS, CORS, an aborted fetch — surfaces as an error
 * with no code. Those must never count against an action's attempts, or a long
 * offline stretch would silently discard everything the user did while away.
 */
export function isServerRejection(error: Error | PostgrestError | undefined): boolean {
  const code = (error as PostgrestError | undefined)?.code
  if (!code) { return false }
  if (TRANSIENT_CODES.has(code)) { return false }
  return !TRANSIENT_SQLSTATE_CLASSES.includes(code.slice(0, 2))
}

/**
 * Whether this failure is the one that exhausts the action's attempts — i.e. the
 * caller should discard it instead of leaving it pending. Counts the failure being
 * reported now, so it can be asked before the attempt is recorded.
 */
export function isUnrecoverable(
  action: Pick<SyncAction, 'attempts'>,
  error: Error | PostgrestError | undefined
): boolean {
  if (!isServerRejection(error)) { return false }
  return (action.attempts ?? 0) + 1 >= MAX_SYNC_ATTEMPTS
}

/**
 * Find the queued action that a sync result refers to. The result carries copies of
 * the actions (`{...action, error}`), not the queued objects themselves, so they can
 * only be matched by identity: same table, same operation, same row.
 */
export function findPendingAction<T extends SyncAction>(
  pending: T[],
  action: Pick<SyncAction, 'table' | 'type'> & { record: { id: string } }
): T | undefined {
  return pending.find((p) =>
    p.table === action.table &&
    p.type === action.type &&
    p.record.id === action.record.id)
}

/**
 * The position of the queued action a sync result refers to, or -1.
 *
 * Same field-matching rule as `findPendingAction`, plus `updated_at` so a queue holding
 * more than one operation for a row removes the one that actually synced. Matching by
 * reference is not an option even for `applied`/`rejected`, which really do carry the
 * queued objects: the caller compares them against `state.pending`, an Immer draft that
 * returns a new proxy for every element read, so `indexOf` is always -1 there.
 */
export function findPendingActionIndex<T extends SyncAction>(
  pending: T[],
  action: Pick<SyncAction, 'table' | 'type'> & { record: { id: string, updated_at: string } }
): number {
  return pending.findIndex((p) =>
    p.table === action.table &&
    p.type === action.type &&
    p.record.id === action.record.id &&
    p.record.updated_at === action.record.updated_at)
}

/**
 * A record the server refuses. Kept for two reasons: sync-down consults it so it
 * doesn't re-queue the record forever, and it's the standing answer to "what did
 * this client fail to upload?" — enough to drive a user-facing indicator on the
 * affected row, which is why the operation and reason are recorded alongside the id.
 */
export type UnsyncableRecord = {
  id: string
  /** The version we gave up on. A newer edit gets a fresh set of attempts. */
  updated_at: string
  /** Which operation was refused — the difference between "not saved" and "not deleted". */
  type: SyncAction['type']
  /** The server's error code, for debugging and for grouping the Sentry report. */
  code?: string
  /**
   * The server's error message. Deliberately NOT the PostgrestError `details`,
   * which can quote the offending column values and therefore PHI; the message
   * itself names constraints and columns only. This state is reported to Sentry
   * through redactSyncSlice, so it has to stay free of record contents.
   */
  message?: string
  discarded_at: string
}

/**
 * Raised (and reported to Sentry) when an action is discarded. Carries the table and
 * error code as fields so the Sentry report can be tagged and grouped by them.
 *
 * Note what this deliberately does NOT do: `serverError` is summarized into the
 * message but never attached as `cause`, so Sentry can't walk the chain to the
 * original PostgrestError. That error's `details` can quote the offending column
 * values ("Key (patient_name)=(...)"), which is PHI we're not allowed to send, and
 * `message` alone names constraints and columns without quoting the row. Chaining
 * the cause here would quietly undo the redaction the rest of this code observes.
 */
export class UnrecoverableSyncError extends Error {
  constructor(
    public readonly table: SyncableTable,
    public readonly operation: SyncAction['type'],
    public readonly recordId: string,
    public readonly code: string | undefined,
    serverError: Error | PostgrestError | undefined
  ) {
    super(`Discarded unsyncable ${table} ${operation} ${recordId} after ` +
      `${MAX_SYNC_ATTEMPTS} attempts: ${code || 'unknown'} ${serverError?.message || ''}`.trim())
    this.name = 'UnrecoverableSyncError'
  }
}
